import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import NavBar from "../components/NavBar";
import Footer from "../components/Footer";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { ArrowUpRight, ShieldCheck } from "lucide-react";

const COPY = {
  client: {
    kicker: "Client Access",
    heading: "Why you can't just 'sign up'.",
    body:
      "To maintain a high success rate with our lending partners, every sign-up is reviewed before access is granted. Your spot is reserved when you create a profile, but access remains locked until your referrer confirms, or until our team approves you after a priority consultation.",
    bullets: [
      "Quality Control, referred businesses only",
      "Referrer Vetting before access is granted",
      "Exclusivity, intensive, white-glove service",
    ],
    roleLabel: "Your role",
    rolePlaceholder: "Founder / CFO / Owner",
    companyLabel: "Company",
    messageLabel: "Tell us about the capital you're raising",
    messagePlaceholder: "Facility size, use of funds, and the assets or business behind it.",
    cta: "Request Client Access",
    footnote: (
      <>No referrer yet? <Link to="/book" className="text-white underline underline-offset-4">Schedule a priority consultation</Link> and we'll personally review your business.</>
    ),
  },
  partner: {
    kicker: "Partner Access",
    heading: "Refer deals into a closed pipeline.",
    body:
      "Partners introduce the businesses we underwrite. Your referrals skip the cold queue and enter the Workspace directly, with live deal stages, documents and the process assistant attached to each file you bring us.",
    bullets: [
      "Your referrals bypass the cold-application queue",
      "Live deal stages and documents in the Workspace",
      "Fee alignment confirmed in writing before closing",
    ],
    roleLabel: "Your role at the firm",
    rolePlaceholder: "Broker / Advisor / Introducer",
    companyLabel: "Firm",
    messageLabel: "What kind of deals do you refer?",
    messagePlaceholder: "Sectors, typical facility size, and volume per year.",
    cta: "Request Partner Access",
    footnote: (
      <>Already referring for another desk? Mention it in your application, we verify every partner before access is granted.</>
    ),
  },
  admin: {
    kicker: "Administrator",
    heading: "Operations console.",
    body:
      "Sign in to approve accounts, review uploads and bookings, manage the Workspace and control whether the public site is live or under maintenance.",
    bullets: [
      "Approve or reject client and partner accounts",
      "Review every upload, booking and signed document",
      "Toggle the public site between live and maintenance",
    ],
    footnote: <>Administrator accounts are provisioned internally, there is no self sign-up.</>,
  },
  underwriter: {
    kicker: "Underwriter Access",
    heading: "Underwrite prepared deals.",
    body:
      "Welcome, and thank you for lending your expertise to D.O.X. Your judgment is the backbone of every deal we close. Sign in to a console built to keep you focused, everything you need to review, decide and move forward, in one calm, productive place.",
    bullets: [
      "See all deals prepared for underwriting",
      "Review documents and clear underwriting gates",
      "Move deals to In Underwriting, Approved or Declined",
    ],
    roleLabel: "Your title",
    rolePlaceholder: "Credit / Underwriting",
    companyLabel: "Firm / desk",
    messageLabel: "Your underwriting focus",
    messagePlaceholder: "Asset classes and facility sizes you underwrite.",
    cta: "Request Underwriter Access",
    footnote: <>Underwriter access is reviewed and approved by an administrator before activation.</>,
  },
};

export default function AuthPage() {
  const { setUser, refresh } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const audience = ["partner", "admin", "underwriter"].includes(params.get("as")) ? params.get("as") : "client";
  const copy = COPY[audience];
  const isAdmin = audience === "admin";

  const [tab, setTab] = useState("login");
  const [li, setLi] = useState({ email: "", password: "" });
  const [liErr, setLiErr] = useState("");
  const [liBusy, setLiBusy] = useState(false);

  const [rg, setRg] = useState({
    name: "", email: "", password: "", company: "", phone: "",
    role_requested: "", message: "", referral_code: "",
    authorized_signatory: false, accepted_legal: false, mailbox_disclosure_accepted: false,
  });
  const [rgErr, setRgErr] = useState("");
  const [rgBusy, setRgBusy] = useState(false);
  const [rgDone, setRgDone] = useState(false);

  const switchAudience = (next) => {
    setParams({ as: next });
    setRgErr(""); setLiErr("");
  };

  const doLogin = async (e) => {
    e.preventDefault();
    setLiBusy(true); setLiErr("");
    try {
      const { data } = await api.post("/auth/login", li);
      setUser(data.user);
      await refresh();
      toast.success("Welcome back");
      const u = data.user;
      if (u.role === "admin") nav("/workspace");
      else if (u.account_type === "underwriter" && u.status === "approved") nav("/workspace");
      else if (u.account_type === "partner" && u.status === "approved") nav(u.workspace_role === "staff" ? "/workspace" : "/partner");
      else nav("/dashboard");
    } catch (e2) {
      setLiErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setLiBusy(false);
    }
  };

  const doRegister = async (e) => {
    e.preventDefault();
    if (!rg.accepted_legal) {
      setRgErr("You must accept the Terms of Service and Privacy Policy to continue.");
      return;
    }
    if (audience === "client") {
      if (!rg.referral_code.trim()) { setRgErr("A referral code is required to create a client profile."); return; }
      if (!rg.phone.trim()) { setRgErr("A phone number is required."); return; }
      if (!rg.authorized_signatory) { setRgErr("You must confirm you are the Authorized Signatory for the Company."); return; }
    }
    if (audience === "partner" && !rg.mailbox_disclosure_accepted) {
      setRgErr("You must acknowledge the Mailbox Access Disclosure to continue.");
      return;
    }
    setRgBusy(true); setRgErr("");
    try {
      await api.post("/auth/register", {
        ...rg,
        account_type: audience,
        referral_code: audience === "client" ? rg.referral_code : undefined,
      });
      setRgDone(true);
      toast.success("Application received");
    } catch (e2) {
      setRgErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setRgBusy(false);
    }
  };

  return (
    <div className="text-white min-h-screen grain">
      <NavBar />
      <section className="pt-32 pb-20 hero-glow">
        <div className="mx-auto max-w-6xl px-6 lg:px-12">
          <div className="flex flex-wrap gap-px bg-white/10 w-fit mb-px">
            {["client", "partner"].map((a) => (
              <button key={a} data-testid={`audience-${a}`} onClick={() => switchAudience(a)}
                className={`px-6 py-3 label-kicker text-xs transition-colors ${
                  audience === a ? "bg-white text-black" : "bg-[#050505] text-white/50 hover:text-white"
                }`}>
                {a === "client" ? "I'm raising capital" : "I refer deals"}
              </button>
            ))}
            {(audience === "admin" || audience === "underwriter") && (
              <button data-testid={`audience-${audience}`}
                className="px-6 py-3 label-kicker text-xs bg-white text-black">
                {audience === "admin" ? "Administrator" : "Underwriter"}
              </button>
            )}
          </div>

          <div className="grid lg:grid-cols-12 gap-px bg-white/10">
            <aside className="lg:col-span-5 bg-[#050505] p-10 lg:p-14">
              <div className="label-kicker">{copy.kicker}</div>
              <h1 className="font-display text-4xl lg:text-5xl font-light mt-4 leading-tight">
                {copy.heading}
              </h1>
              <p className="text-white/60 mt-6 max-w-md">{copy.body}</p>
              <div className="mt-10 space-y-4 text-sm text-white/55">
                {copy.bullets.map((b) => <Row key={b} icon={ShieldCheck} text={b} />)}
              </div>
              <p className="text-xs text-white/35 mt-10">{copy.footnote}</p>
              {(audience === "admin" || audience === "underwriter") && (
                <button data-testid="back-to-partner-link" onClick={() => switchAudience("partner")}
                  className="mt-6 block text-xs text-white/40 hover:text-white underline underline-offset-4">
                  ← Back to partner access
                </button>
              )}
            </aside>

            <div className="lg:col-span-7 bg-[#050505] p-10 lg:p-14">
              <Tabs value={isAdmin ? "login" : tab} onValueChange={setTab}>
                <TabsList className="rounded-none bg-transparent p-0 gap-6 border-b border-white/10">
                  <TabsTrigger data-testid="tab-login" value="login"
                    className="rounded-none data-[state=active]:bg-transparent data-[state=active]:text-white data-[state=active]:border-b data-[state=active]:border-white px-0 pb-3 text-white/60">
                    {audience === "partner" ? "Partner Sign In" : audience === "admin" ? "Admin Sign In" : audience === "underwriter" ? "Underwriter Sign In" : "Client Sign In"}
                  </TabsTrigger>
                  {!isAdmin && audience !== "partner" && (
                    <TabsTrigger data-testid="tab-register" value="register"
                      className="rounded-none data-[state=active]:bg-transparent data-[state=active]:text-white data-[state=active]:border-b data-[state=active]:border-white px-0 pb-3 text-white/60">
                      Sign Up
                    </TabsTrigger>
                  )}
                </TabsList>

                <TabsContent value="login" className="mt-8">
                  {audience === "partner" && (
                    <p data-testid="partner-login-welcome" className="text-white/60 text-sm mb-6 max-w-md">
                      Welcome back. Sign in to track your referred deals and their progress through the D.O.X pipeline.
                    </p>
                  )}
                  <form onSubmit={doLogin} className="space-y-5 max-w-md">
                    <Field label="Email">
                      <Input data-testid="login-email" type="email" required value={li.email}
                        onChange={(e) => setLi((s) => ({ ...s, email: e.target.value }))}
                        className="rounded-none bg-black/40 border-white/15" />
                    </Field>
                    <Field label="Password">
                      <Input data-testid="login-password" type="password" required value={li.password}
                        onChange={(e) => setLi((s) => ({ ...s, password: e.target.value }))}
                        className="rounded-none bg-black/40 border-white/15" />
                    </Field>
                    {liErr && <p data-testid="login-error" className="text-red-400 text-sm">{liErr}</p>}
                    <Button data-testid="login-submit" disabled={liBusy}
                      className="rounded-none bg-white text-black hover:bg-white/90 h-11 px-6 btn-shimmer">
                      {liBusy ? "Signing in…" : "Sign In"} <ArrowUpRight className="ml-2 h-4 w-4" strokeWidth={1.25} />
                    </Button>
                  </form>
                  {audience === "client" && (
                    <GoogleButton audience={audience} label="Continue with Google" />
                  )}
                </TabsContent>

                <TabsContent value="register" className="mt-8">
                  {rgDone ? (
                    <div data-testid="register-success" className="max-w-md">
                      <div className="label-kicker">Profile Reserved</div>
                      <h3 className="font-display text-3xl mt-3">Your spot is reserved.</h3>
                      <p className="text-white/60 mt-4">
                        Your {audience} profile has been created and our team has been notified.
                        Access remains <span className="text-white">locked</span> until it is
                        approved. A confirmation has been sent to{" "}
                        <span className="text-white">{rg.email}</span>.
                      </p>
                      <Button onClick={() => setTab("login")} className="mt-8 rounded-none bg-white text-black hover:bg-white/90">
                        Back to Sign In
                      </Button>
                    </div>
                  ) : (
                    <form onSubmit={doRegister} className="grid sm:grid-cols-2 gap-5 max-w-2xl">
                      {audience === "client" && (
                        <Field label="Referral code (required)" className="sm:col-span-2">
                          <Input data-testid="reg-referral" required value={rg.referral_code}
                            placeholder=""
                            onChange={(e) => setRg((s) => ({ ...s, referral_code: e.target.value.toUpperCase() }))}
                            className="rounded-none bg-black/40 border-white/15" />
                          <p className="text-xs text-white/40 mt-1">A partner introduction code is required before any other field. No code yet? <Link to="/book" className="text-white underline underline-offset-4">Book a discovery call</Link>.</p>
                        </Field>
                      )}
                      <Field label="Full name">
                        <Input data-testid="reg-name" required value={rg.name}
                          onChange={(e) => setRg((s) => ({ ...s, name: e.target.value }))}
                          className="rounded-none bg-black/40 border-white/15" />
                      </Field>
                      <Field label="Email">
                        <Input data-testid="reg-email" type="email" required value={rg.email}
                          onChange={(e) => setRg((s) => ({ ...s, email: e.target.value }))}
                          className="rounded-none bg-black/40 border-white/15" />
                      </Field>
                      <Field label={audience === "client" ? "Phone" : "Phone (optional)"}>
                        <Input data-testid="reg-phone" required={audience === "client"} value={rg.phone}
                          placeholder="+1 555 123 4567"
                          onChange={(e) => setRg((s) => ({ ...s, phone: e.target.value }))}
                          className="rounded-none bg-black/40 border-white/15" />
                      </Field>
                      <Field label={copy.companyLabel}>
                        <Input data-testid="reg-company" required={audience === "client"} value={rg.company}
                          onChange={(e) => setRg((s) => ({ ...s, company: e.target.value }))}
                          className="rounded-none bg-black/40 border-white/15" />
                      </Field>
                      <Field label={copy.roleLabel} className="sm:col-span-2">
                        {audience === "partner" ? (
                          <Select value={rg.role_requested || undefined} onValueChange={(v) => setRg((s) => ({ ...s, role_requested: v }))}>
                            <SelectTrigger data-testid="reg-role" className="rounded-none bg-black/40 border-white/15">
                              <SelectValue placeholder="Partner / Lender / Introducer" />
                            </SelectTrigger>
                            <SelectContent>
                              {["Partner", "Lender", "Introducer"].map((r) => (
                                <SelectItem key={r} value={r}>{r}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Input data-testid="reg-role" required={audience === "client"} value={rg.role_requested}
                            placeholder={copy.rolePlaceholder}
                            onChange={(e) => setRg((s) => ({ ...s, role_requested: e.target.value }))}
                            className="rounded-none bg-black/40 border-white/15" />
                        )}
                      </Field>
                      <Field label="Password" className="sm:col-span-2">
                        <Input data-testid="reg-password" type="password" required minLength={8} value={rg.password}
                          onChange={(e) => setRg((s) => ({ ...s, password: e.target.value }))}
                          className="rounded-none bg-black/40 border-white/15" />
                        <p className="text-xs text-white/40 mt-1">Minimum 8 characters.</p>
                      </Field>
                      <Field label={copy.messageLabel} className="sm:col-span-2">
                        <Textarea data-testid="reg-message" rows={3} value={rg.message}
                          onChange={(e) => setRg((s) => ({ ...s, message: e.target.value }))}
                          className="rounded-none bg-black/40 border-white/15"
                          placeholder={copy.messagePlaceholder} />
                      </Field>
                      {audience === "client" && (
                        <label data-testid="reg-signatory-label" className="sm:col-span-2 flex items-start gap-3 cursor-pointer select-none border border-white/10 bg-black/20 p-4">
                          <input data-testid="reg-signatory-checkbox" type="checkbox" required checked={rg.authorized_signatory}
                            onChange={(e) => setRg((s) => ({ ...s, authorized_signatory: e.target.checked }))}
                            className="mt-0.5 h-4 w-4 shrink-0 accent-[hsl(var(--gold))]" />
                          <span className="text-xs text-white/60 leading-relaxed">
                            I am the Authorized Signatory for the Company listed above.
                          </span>
                        </label>
                      )}
                      <label data-testid="reg-legal-label" className="sm:col-span-2 flex items-start gap-3 cursor-pointer select-none border border-white/10 bg-black/20 p-4">
                        <input data-testid="reg-legal-checkbox" type="checkbox" required checked={rg.accepted_legal}
                          onChange={(e) => setRg((s) => ({ ...s, accepted_legal: e.target.checked }))}
                          className="mt-0.5 h-4 w-4 shrink-0 accent-[hsl(var(--gold))]" />
                        <span className="text-xs text-white/60 leading-relaxed">
                          I have read and agree to the{" "}
                          <Link to="/legal/terms" target="_blank" rel="noreferrer" data-testid="reg-legal-terms-link" className="text-white underline underline-offset-4">Terms of Service</Link>
                          {" "}and{" "}
                          <Link to="/legal/privacy" target="_blank" rel="noreferrer" data-testid="reg-legal-privacy-link" className="text-white underline underline-offset-4">Privacy Policy</Link>
                          {" "}(version June 2026). I understand my acceptance is an electronic signature and will be recorded with a timestamp and my IP address.
                        </span>
                      </label>
                      {audience === "partner" && (
                        <label data-testid="reg-mailbox-label" className="sm:col-span-2 flex items-start gap-3 cursor-pointer select-none border border-white/10 bg-black/20 p-4">
                          <input data-testid="reg-mailbox-checkbox" type="checkbox" checked={rg.mailbox_disclosure_accepted}
                            onChange={(e) => setRg((s) => ({ ...s, mailbox_disclosure_accepted: e.target.checked }))}
                            className="mt-0.5 h-4 w-4 shrink-0 accent-[hsl(var(--gold))]" />
                          <span className="text-xs text-white/60 leading-relaxed">
                            I acknowledge the{" "}
                            <Link to="/legal/mailbox-access" target="_blank" rel="noreferrer" data-testid="reg-mailbox-link" className="text-white underline underline-offset-4">Mailbox Access Disclosure</Link>:
                            {" "}D.O.X Capital will assign me a @thedoxs.com business inbox and may access, monitor, send, and receive email through it for compliance and transaction-coordination purposes.
                          </span>
                        </label>
                      )}
                      {rgErr && <p data-testid="register-error" className="sm:col-span-2 text-red-400 text-sm">{rgErr}</p>}
                      <div className="sm:col-span-2">
                        <Button data-testid="reg-submit" disabled={rgBusy || !rg.accepted_legal || (audience === "client" && !rg.authorized_signatory) || (audience === "partner" && !rg.mailbox_disclosure_accepted)}
                          className="rounded-none bg-white text-black hover:bg-white/90 h-11 px-6 btn-shimmer disabled:opacity-40">
                          {rgBusy ? "Submitting…" : copy.cta} <ArrowUpRight className="ml-2 h-4 w-4" strokeWidth={1.25} />
                        </Button>
                      </div>
                    </form>
                  )}
                  {!rgDone && (audience === "client" || audience === "partner") && (
                    <GoogleButton audience={audience} label="Sign up with Google" />
                  )}
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="label-kicker">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
function Row({ icon: Icon, text }) {
  return (
    <div className="flex items-center gap-3">
      <Icon className="h-4 w-4 text-[hsl(var(--gold))]" strokeWidth={1.25} />
      <span>{text}</span>
    </div>
  );
}

function GoogleButton({ audience, label }) {
  const go = () => {
    sessionStorage.setItem("dox_google_audience", audience);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <div className="max-w-md">
      <div className="flex items-center gap-3 my-6">
        <div className="h-px flex-1 bg-white/10" />
        <span className="text-[10px] tracking-widest uppercase text-white/30">or</span>
        <div className="h-px flex-1 bg-white/10" />
      </div>
      <button type="button" data-testid="google-signin-btn" onClick={go}
        className="w-full flex items-center justify-center gap-3 border border-white/15 bg-white/[0.03] hover:bg-white/[0.07] h-11 text-sm text-white/90 transition-colors">
        <GoogleIcon /> {label}
      </button>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}
